import { AnalyticsBrowser, Context } from '@segment/analytics-next';
import { isbot } from 'isbot';
import LogRocket from 'logrocket';
import logRocketReact from 'logrocket-react';

import { registerModalOpened } from '@/redux/modals/middleware';
import { selectAccountId, selectDeskId } from '@/redux/session/selectors';
import { getStore } from '@/redux/store';
import { selectIframeView } from '@/redux/user/selectors';
import { Account } from '@/shared/types/account';
import { RoleType } from '@/shared/types/member';
import { PageName, EventName, EventProps, UserRole } from '@/shared/types/segment';
import { Preferences, User } from '@/shared/types/user';
import { ADMIN, BILLING, BUILDER, CHAT_AGENT, CHAT_MANAGER } from '@/util/constants';
import { isDevOrLocal } from '@/util/environment';
import { extractResourceId, normalizePath } from '@/util/normalizePath';

import { PAGE_TAXONOMY } from './pageTaxonomy';
import { registerPathSanitizer } from './sanitizePayload';
import { enterSection, PageSnapshot, registerSectionDwell } from './sectionDwell';
import { registerEventValidation } from './validateEvent';

// logrocket-react is CommonJS, and Vite hands over its whole module object, so the setup
// function is on `.default`.
const setupLogRocketReact = (logRocketReact as unknown as { default: typeof logRocketReact })
  .default;

let userIdentified = false;

/** Written by `pageView`, read by every `trackEvent`. */
let currentPage: PageSnapshot | null = null;

/**
 * The last view reported, so the same URL reported twice in a row counts once.
 * Two components can own one screen — `PaidFeatureGate` renders the gated page
 * until billing resolves, then replaces it with the paywall — and both report.
 * Keyed on the pathname, so the next record on a detail route still counts.
 */
let lastView: { pageName: PageName; pathname: string } | null = null;

const INTERNAL_EMAIL_DOMAIN = '@moveo.ai';

/** Whether an email belongs to a Moveo employee. Domains are case-insensitive. */
export const isInternalEmail = (email?: string): boolean =>
  Boolean(email?.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN));

let internalUser: boolean | undefined;
let identifiedUserId: string | undefined;

/** The user analytics.js last identified, which survives the logout reload. */
const persistedUserId = (): string | undefined => {
  try {
    const stored = window.localStorage.getItem('ajs_user_id');
    return stored ? (JSON.parse(stored) as string) : undefined;
  } catch {
    // Private mode, a disabled store, or a value analytics.js did not write.
    return undefined;
  }
};

// Falls back to the server-injected user, so it is correct before `identify`.
const isInternalUser = (): boolean => {
  if (internalUser !== undefined) {
    return internalUser;
  }
  return isInternalEmail(window.MOVEO_REDUX_STATE?.user?.user?.email);
};

/**
 * Set by `trackAccount`; the plan is not in the redux store. Kept next to the
 * account it describes, so an account switch cannot report the previous plan.
 */
let accountPlan: { account_id: string; plan_id: string } | undefined;

/**
 * The system roles we report, most privileged first. A member holding several is
 * reported as the first one they hold, so `role` stays a single bounded value.
 */
const REPORTED_ROLES: [roleId: string, role: UserRole][] = [
  [ADMIN, 'admin'],
  [BUILDER, 'builder'],
  [CHAT_MANAGER, 'chat_manager'],
  [CHAT_AGENT, 'chat_agent'],
  [BILLING, 'billing'],
];

/**
 * Owner lives on the membership rather than in `role_ids`, so it is checked
 * separately. Anything we do not report by name — an account's own role, or the
 * retired `Reader` and `Writer` — counts as `custom`, because a per-account id
 * says nothing in a group-by and its name identifies the account.
 */
const deriveRole = (roleIds: string[] = [], accountRole?: string): UserRole => {
  if (accountRole === RoleType.OWNER) {
    return 'owner';
  }
  const reported = REPORTED_ROLES.find(([roleId]) => roleIds.includes(roleId))?.[1];
  return reported ?? (roleIds.length ? 'custom' : 'none');
};

/**
 * Set by `trackRoles`; roles are not in the redux store. Kept next to the
 * account they belong to, so an account switch cannot report the role the user
 * held in the previous account.
 */
let accountRoles: { account_id: string; role: UserRole } | undefined;

/** Sent on every page view and every event. See docs/analytics.md. */
const globalContext = () => {
  const state = getStore().getState();
  const accountId = selectAccountId(state) ?? undefined;
  return {
    moveo_region: window.MOVEO_REGION,
    is_internal: isInternalUser(),
    slug: window.X_MOVEO_ACCOUNT_SLUG,
    account_id: accountId,
    desk_id: selectDeskId(state) ?? undefined,
    plan_id: accountPlan && accountPlan.account_id === accountId ? accountPlan.plan_id : undefined,
    role: accountRoles && accountRoles.account_id === accountId ? accountRoles.role : undefined,
    is_iframe: selectIframeView(state),
  };
};

/**
 * Layers the caller's properties over the page and global context.
 *
 * The call site wins, but `undefined` reads as "not supplied", so an optional id
 * cannot blank a value the context already knows.
 */
const withContext = (properties?: EventProps): EventProps => {
  const merged: Record<string, unknown> = { ...globalContext(), ...currentPage };
  for (const [key, value] of Object.entries(properties ?? {})) {
    if (value !== undefined) {
      merged[key] = value;
    }
  }
  return merged as EventProps;
};

const loadAnalytics = () => {
  if (window.SEGMENT_KEY && !isbot(navigator.userAgent)) {
    return AnalyticsBrowser.load(
      {
        writeKey: window.SEGMENT_KEY,
        // Both hosts are on every ad-blocker list, and this is a console for
        // technical users. Set these to a first-party subdomain and the requests
        // stop looking like third-party tracking; unset, nothing changes.
        ...(window.SEGMENT_CDN_URL && { cdnURL: window.SEGMENT_CDN_URL }),
      },
      {
        integrations: {
          'Segment.io': {
            ...(window.SEGMENT_API_HOST && { apiHost: window.SEGMENT_API_HOST }),
            // `keepalive` is what lets an in-flight request survive the unload
            // it was fired on. See sectionDwell.ts, which depends on it.
            deliveryStrategy: { strategy: 'standard', config: { keepalive: true } },
          },
        },
      }
    );
  }

  // Return a mock to avoid sending bot events to segment.
  return {
    page: console.debug,
    track: console.debug,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    group: async (): Promise<any> => console.debug,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    identify: async (): Promise<any> => console.debug,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reset: async (): Promise<any> => console.debug,
  };
};

const analytics = loadAnalytics();

/**
 * Reports something the user did.
 *
 * Pass only what the event is about — the entity ids. The page taxonomy, the
 * account and the region arrive on their own.
 *
 * @param name - A member of `EventName`; never look one up dynamically
 * @param properties - Entity ids and bounded values, from `EventProps`
 */
export const trackEvent = (name: EventName, properties?: EventProps) => {
  return analytics.track(name, withContext(properties));
};

/**
 * Drops the page context when the page that set it unmounts, so an untracked
 * route reports no page rather than the previous one. Call it from the
 * `useSegments` cleanup only.
 *
 * The section visit is left running on purpose: only `enterSection` knows the
 * destination's section, so only it can tell a move inside a section from a move
 * out of one.
 *
 * @param pageName - The page giving up the context; ignored if another page owns it
 */
export const clearPageContext = (pageName: PageName) => {
  if (currentPage?.page_name === pageName) {
    currentPage = null;
  }
};

// First, so the validator judges the payload that actually leaves.
registerPathSanitizer(analytics);

if (isDevOrLocal()) {
  registerEventValidation(analytics);
}

registerSectionDwell((name, props) => trackEvent(name, props));

registerModalOpened((kind) => trackEvent(EventName.ModalOpened, { modal_name: kind }));

/**
 * Reports a screen the user landed on, and takes ownership of the page context
 * every event fired from that screen will carry.
 *
 * Call it through `useSegments`, never directly: the hook owns the cleanup that
 * gives the context back.
 *
 * @param pageName - Must have an entry in `PAGE_TAXONOMY`
 * @param properties - Rarely needed; the taxonomy and the ids come from the route
 */
export const pageView = (pageName: PageName, properties?: EventProps) => {
  const { section, type, path: staticTemplate } = PAGE_TAXONOMY[pageName];
  const pathname = window.location.pathname;
  const pathTemplate = staticTemplate ?? normalizePath(pathname);

  if (lastView?.pageName === pageName && lastView.pathname === pathname) {
    return;
  }
  lastView = { pageName, pathname };

  const snapshot: PageSnapshot = {
    page_name: pageName,
    page_section: section,
    page_type: type,
    path_template: pathTemplate,
  };

  // Before the page call, so an event fired in the same commit inherits it.
  currentPage = snapshot;
  enterSection(snapshot, trackEvent);

  return analytics.page(
    section,
    pageName,
    withContext({
      ...properties,
      resource_id: staticTemplate ? undefined : extractResourceId(pathname),
    })
  );
};

/**
 * Reports which account the user is working in, as a Segment group, and is the
 * only source of `plan_id`.
 *
 * @see https://segment.com/docs/connections/spec/group/
 */
export const trackAccount = (account: Account): Promise<Context> => {
  accountPlan = { account_id: account.account_id, plan_id: account.plan_id };
  return analytics.group(account.account_id, {
    name: account.name,
    // `createdAt` and `plan` are Segment's reserved group traits, which every
    // destination maps on its own. The two beside them are what we already send;
    // drop those once nothing reads them.
    createdAt: account.created,
    created: account.created,
    plan: account.plan_id,
    plan_id: account.plan_id,
    slug: account.slug,
    external_customer_id: account.external_customer_id,
    current_period_ends: account.current_period_ends,
  });
};

/**
 * Reports the role the user holds in the account they are working in, and is the
 * only source of `role`. Call it beside `trackAccount`.
 *
 * The role rides on every event, because Amplitude cannot break an event down by
 * a user trait, and goes on the user profile as well, so a chart can count
 * people by role without reaching into an event.
 *
 * @param accountId - The account the roles belong to
 * @param roleIds - `Member.role_ids`, the roles granted to the user directly
 * @param accountRole - The membership role, which is where `owner` lives
 */
export const trackRoles = (accountId: string, roleIds?: string[], accountRole?: string) => {
  accountRoles = { account_id: accountId, role: deriveRole(roleIds, accountRole) };

  // Enriches the profile of a user already identified; `identifyUser` reads the
  // stash itself, so the other order is covered too.
  if (identifiedUserId) {
    analytics.identify(identifiedUserId, { role: accountRoles.role });
  }
};

/**
 * Forgets the signed-in user, so the next person on this browser starts as
 * someone else. Call it when the user logs out.
 */
export const resetIdentity = () => {
  identifiedUserId = undefined;
  internalUser = undefined;
  accountPlan = undefined;
  accountRoles = undefined;
  currentPage = null;
  lastView = null;
  return analytics.reset();
};

/**
 * Says who the user is. Safe to call again to enrich: Amplitude does not bill
 * identify calls.
 *
 * @param user - The signed-in user
 * @param preferences - Optional, so the first call need not wait for them
 * @param trackUserInLogRocket - Enrols the user in session replay, once
 */
export const identifyUser = async (
  user: User,
  preferences?: Preferences,
  trackUserInLogRocket = false
): Promise<Context> => {
  // analytics.js keeps `ajs_user_id` across the logout reload, so a shared
  // browser would attribute the next person's events to the previous user.
  const previousUserId = identifiedUserId ?? persistedUserId();
  const isDifferentUser = Boolean(previousUserId && previousUserId !== user.user_id);

  // Everything below happens before the first `await`: the caller does not wait,
  // so a page view can run while `reset` is still in flight.
  identifiedUserId = user.user_id;
  internalUser = isInternalEmail(user.email);
  if (isDifferentUser) {
    accountPlan = undefined;
    accountRoles = undefined;
    currentPage = null;
    lastView = null;
    await analytics.reset();
  }

  // Segment identify
  const context = await analytics.identify(user.user_id, {
    email: user.email,
    name: user.name,
    // Same as the group traits: `createdAt` and `title` are the reserved names.
    createdAt: user.created,
    created: user.created,
    is_internal: internalUser,
    ...(accountRoles && { role: accountRoles.role }),
    ...(preferences && {
      language: preferences.language || 'en',
      timezone: preferences.timezone,
      title: preferences.job as string,
      job: preferences.job,
      industry: preferences.industry as string,
    }),
  });

  if (!window.LOGROCKET_ID) {
    return context;
  }

  // LogRocket identify
  if (!userIdentified && trackUserInLogRocket) {
    LogRocket.init(window.LOGROCKET_ID);
    setupLogRocketReact();
    userIdentified = true;
    LogRocket.identify(user.user_id, { name: user.name });

    // Two calls on purpose: the event is the per-session join we already have,
    // the trait puts the same link on the user profile, where a chart reaches it
    // without hunting for one event inside the session.
    LogRocket.getSessionURL(function (sessionURL) {
      analytics.track('LogRocket', { sessionURL: sessionURL });
      analytics.identify(user.user_id, { logrocket_session_url: sessionURL });
    });
  }

  return context;
};
